import { isDefined, NbSelectItemOption } from '@eih-netbroker/common';
import { FormControl, FormHelperText, InputLabel, MenuItem, Select, SelectProps } from '@mui/material';
import { ReactNode } from 'react';
import { cssWhen } from '../../utils/themeUtils';
import { NbLoader } from '../loader/Loader';
import classes from './Select.module.scss';

export type NbSelectProps<T> = SelectProps<T> & {
  options: Array<NbSelectItemOption<T>>;
  errorMessage?: string;
  enforceErrorBordersWithoutMessage?: boolean;
  isLoading?: boolean;
  emptyValueText?: string;
  getOptionTestId?: (option: NbSelectItemOption<T>) => string | undefined;
};

const renderValue = <T,>({
  options,
  emptyValueText,
}: {
  options: Array<NbSelectItemOption<T>>;
  emptyValueText?: string;
}): ((value: T) => ReactNode) => {
  return (value: T) => {
    const isEmpty = !isDefined(value) || value === '';

    if (isEmpty) {
      return emptyValueText;
    }
    const item = options.find(option => option.id === value);
    if (!item) {
      return emptyValueText;
    } else {
      return (
        <>
          {item.logo && typeof item.logo === 'string' && (
            <img
              className={classes.nbSelectLogo}
              src={item.logo}
              alt={typeof item.name === 'string' ? item.name : ''}
            />
          )}
          {item.logo && typeof item.logo !== 'string' && item.logo}
          {item.name}
        </>
      );
    }
  };
};

export const NbSelect = <T,>({
  options,
  errorMessage,
  value,
  defaultValue,
  className,
  readOnly,
  disabled,
  isLoading,
  emptyValueText,
  enforceErrorBordersWithoutMessage,
  getOptionTestId,
  ...props
}: NbSelectProps<T>) => {
  const isSelectDisabled = disabled || isLoading;
  const isEmpty = !isDefined(value) || value === '';

  return (
    <FormControl
      className={cssWhen(
        classes.nbSelect,
        [errorMessage ?? (props.error && enforceErrorBordersWithoutMessage), classes.nbSelectError],
        [isEmpty, classes.nbSelectPlaceholder],
        className,
      )}
    >
      <InputLabel>{props.label}</InputLabel>
      <div className={classes.wrapper}>
        <Select
          value={value}
          defaultValue={defaultValue}
          readOnly={readOnly}
          disabled={isSelectDisabled}
          displayEmpty={isDefined(emptyValueText)}
          renderValue={renderValue({ options, emptyValueText })}
          {...props}
          classes={{ select: classes.nbSelect, nativeInput: classes.nbInput }}
        >
          {options.map((option, index) => (
            <MenuItem
              key={index}
              value={option.id as number}
              data-testid={getOptionTestId?.(option)}
            >
              {/* Option logo is a URL */}
              {option.logo && typeof option.logo === 'string' && (
                <img
                  className={classes.nbSelectLogo}
                  src={option.logo}
                  alt={typeof option.name === 'string' ? option.name : ''}
                />
              )}
              {/* Option logo is a ReactNode */}
              {option.logo && typeof option.logo !== 'string' && option.logo}
              {option.name}
            </MenuItem>
          ))}
        </Select>
        {isLoading && (
          <div className={classes.loader}>
            <NbLoader size={'small'} />
          </div>
        )}
      </div>
      {errorMessage && (
        <FormHelperText
          className={classes.nbSelectError}
          error
        >
          {errorMessage}
        </FormHelperText>
      )}
    </FormControl>
  );
};
